import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { Product, Team, ValueStream } from './types';
import { at, pad, round } from './support';

/**
 * Delivery teams.
 *
 * Teams are the unit that actually holds capacity and cost, and they rarely map
 * one-to-one onto products: the crew platform team below carries four. That is
 * why team cost has to be allocated rather than assigned, and why the workforce
 * roll-up has to de-duplicate the same way the application roll-up does.
 */

interface CuratedTeamSeed {
  code: string;
  name: string;
  valueStreamCode: string;
  leadPersonCode: string;
  productCodes: string[];
  internalFte: number;
  contractorFte: number;
  vacancyCount: number;
}

const CURATED_TEAMS: readonly CuratedTeamSeed[] = [
  {
    code: 'TEAM-CREW-PLAT',
    name: 'Crew Platform',
    valueStreamCode: 'VS-CREW',
    leadPersonCode: 'PPL-0056',
    productCodes: ['PRD-CDR', 'PRD-CRWP', 'PRD-CRTR'],
    internalFte: 34,
    contractorFte: 11,
    vacancyCount: 4,
  },
  {
    code: 'TEAM-CREW-RECOVERY',
    name: 'Crew Recovery Squad',
    valueStreamCode: 'VS-CREW',
    leadPersonCode: 'PPL-0055',
    productCodes: ['PRD-CDR'],
    internalFte: 18,
    contractorFte: 6,
    vacancyCount: 1,
  },
  {
    code: 'TEAM-SHOP',
    name: 'Shopping & Offer',
    valueStreamCode: 'VS-BOOK',
    leadPersonCode: 'PPL-0041',
    productCodes: ['PRD-BOOK'],
    internalFte: 42,
    contractorFte: 9,
    vacancyCount: 3,
  },
  {
    code: 'TEAM-ORDER',
    name: 'Order & Servicing',
    valueStreamCode: 'VS-BOOK',
    leadPersonCode: 'PPL-0042',
    productCodes: ['PRD-TRIP', 'PRD-BOOK'],
    internalFte: 31,
    contractorFte: 14,
    vacancyCount: 2,
  },
  {
    code: 'TEAM-AIRPORT',
    name: 'Airport Platform',
    valueStreamCode: 'VS-ARPT',
    leadPersonCode: 'PPL-0038',
    productCodes: ['PRD-CHKB', 'PRD-ARMG'],
    internalFte: 28,
    contractorFte: 12,
    vacancyCount: 5,
  },
  {
    code: 'TEAM-BAG',
    name: 'Baggage Engineering',
    valueStreamCode: 'VS-ARPT',
    leadPersonCode: 'PPL-0037',
    productCodes: ['PRD-BAGJ'],
    internalFte: 16,
    contractorFte: 15,
    vacancyCount: 6,
  },
  {
    code: 'TEAM-TURN',
    name: 'Turn & Ground',
    valueStreamCode: 'VS-ARPT',
    leadPersonCode: 'PPL-0036',
    productCodes: ['PRD-TURN'],
    internalFte: 14,
    contractorFte: 7,
    vacancyCount: 2,
  },
  {
    code: 'TEAM-DISPATCH',
    name: 'Dispatch & Flight Planning',
    valueStreamCode: 'VS-FLTOPS',
    leadPersonCode: 'PPL-0039',
    productCodes: ['PRD-DISP', 'PRD-WBAL'],
    internalFte: 22,
    contractorFte: 5,
    vacancyCount: 1,
  },
  {
    code: 'TEAM-FUEL',
    name: 'Fuel & Efficiency Analytics',
    valueStreamCode: 'VS-FLTOPS',
    leadPersonCode: 'PPL-0040',
    productCodes: ['PRD-FUEL'],
    internalFte: 9,
    contractorFte: 3,
    vacancyCount: 0,
  },
  {
    code: 'TEAM-NETPLAN',
    name: 'Network Planning Systems',
    valueStreamCode: 'VS-NETPLAN',
    leadPersonCode: 'PPL-0024',
    productCodes: ['PRD-NETP'],
    internalFte: 12,
    contractorFte: 4,
    vacancyCount: 1,
  },
  {
    code: 'TEAM-MTCE',
    name: 'Maintenance Systems',
    valueStreamCode: 'VS-MTCE',
    leadPersonCode: 'PPL-0023',
    productCodes: ['PRD-MNTP'],
    internalFte: 19,
    contractorFte: 9,
    vacancyCount: 3,
  },
  {
    code: 'TEAM-LOYALTY',
    name: 'Loyalty Engineering',
    valueStreamCode: 'VS-LOYALTY',
    leadPersonCode: 'PPL-0043',
    productCodes: ['PRD-LOYL', 'PRD-LPST'],
    internalFte: 26,
    contractorFte: 8,
    vacancyCount: 2,
  },
  {
    code: 'TEAM-CARE',
    name: 'Customer Care Platform',
    valueStreamCode: 'VS-CARE',
    leadPersonCode: 'PPL-0026',
    productCodes: ['PRD-NOTF', 'PRD-DSCM'],
    internalFte: 24,
    contractorFte: 10,
    vacancyCount: 4,
  },
  {
    code: 'TEAM-PRICING',
    name: 'Pricing & Revenue Systems',
    valueStreamCode: 'VS-PRM',
    leadPersonCode: 'PPL-0028',
    productCodes: ['PRD-PRMG'],
    internalFte: 21,
    contractorFte: 6,
    vacancyCount: 1,
  },
  {
    code: 'TEAM-RETAIL',
    name: 'Ancillary Retailing',
    valueStreamCode: 'VS-RETAIL',
    leadPersonCode: 'PPL-0029',
    productCodes: ['PRD-ANCL'],
    internalFte: 17,
    contractorFte: 11,
    vacancyCount: 3,
  },
  {
    code: 'TEAM-CARGO',
    name: 'Cargo Systems',
    valueStreamCode: 'VS-CARGO',
    leadPersonCode: 'PPL-0030',
    productCodes: ['PRD-CRGO'],
    internalFte: 13,
    contractorFte: 6,
    vacancyCount: 2,
  },
  {
    code: 'TEAM-REVACC',
    name: 'Revenue Accounting Engineering',
    valueStreamCode: 'VS-FIN',
    leadPersonCode: 'PPL-0044',
    productCodes: ['PRD-REVA'],
    internalFte: 15,
    contractorFte: 18,
    vacancyCount: 7,
  },
  {
    code: 'TEAM-PAY',
    name: 'Payments Platform',
    valueStreamCode: 'VS-FIN',
    leadPersonCode: 'PPL-0045',
    productCodes: ['PRD-PAYB'],
    internalFte: 23,
    contractorFte: 5,
    vacancyCount: 1,
  },
  {
    code: 'TEAM-DATA',
    name: 'Data Platform',
    valueStreamCode: 'VS-DATAAI',
    leadPersonCode: 'PPL-0033',
    productCodes: ['PRD-C360'],
    internalFte: 38,
    contractorFte: 13,
    vacancyCount: 5,
  },
];

export function buildTeams(
  asOf: string,
  rng: Rng,
  products: readonly Product[],
  valueStreams: readonly ValueStream[],
): Team[] {
  const teamRng = rng.fork('teams');
  const stamp = provenanceFactory(asOf, rng.fork('teams-provenance'));
  const teams: Team[] = [];

  const push = (seed: CuratedTeamSeed, index: number): void => {
    teams.push({
      id: idFor('Team', seed.code),
      businessCode: seed.code,
      name: seed.name,
      productCodes: seed.productCodes,
      valueStreamCode: seed.valueStreamCode,
      leadPersonCode: seed.leadPersonCode,
      internalFte: seed.internalFte,
      contractorFte: seed.contractorFte,
      // Two-week iteration capacity, ~4.5 points per FTE, discounted for churn.
      capacityPerIteration: Math.round(
        (seed.internalFte + seed.contractorFte * 0.85) * teamRng.float(4.0, 5.2),
      ),
      vacancyCount: seed.vacancyCount,
      provenance: stamp(SOURCE_SYSTEMS.hcm, `HCM-TEAM-${pad(index + 1, 4)}`, {
        meanAgeDays: 14,
        meanConfidence: 0.88,
        dataClassification: 'Confidential',
      }),
    });
  };

  CURATED_TEAMS.forEach(push);

  // Generated teams for the product tail, one per two or three products so the
  // workforce screens have a realistic long tail rather than 19 tidy rows.
  const generated = products.filter((p) => !p.isCurated && p.lifecycle === 'Active');
  const streamByCode = new Map(valueStreams.map((v) => [v.businessCode, v]));
  let bucket: string[] = [];
  let index = 0;
  for (const product of generated) {
    bucket.push(product.businessCode);
    if (bucket.length >= teamRng.int(1, 3)) {
      const stream = streamByCode.get(product.valueStreamCode);
      const internalFte = teamRng.int(5, 26);
      const contractorFte = teamRng.int(0, 14);
      const code = `TEAM-G${pad(index + 1, 3)}`;
      teams.push({
        id: idFor('Team', code),
        businessCode: code,
        name: `${stream?.name ?? 'Shared'} Squad ${pad(index + 1, 2)}`,
        productCodes: bucket,
        valueStreamCode: product.valueStreamCode,
        leadPersonCode: stream?.ownerPersonCode ?? 'PPL-0005',
        internalFte,
        contractorFte,
        capacityPerIteration: Math.round(
          (internalFte + contractorFte * 0.85) * teamRng.float(3.6, 5.0),
        ),
        vacancyCount: teamRng.int(0, 5),
        provenance: stamp(SOURCE_SYSTEMS.hcm, `HCM-TEAM-G${pad(index + 1, 4)}`, {
          meanAgeDays: 17,
          meanConfidence: 0.79,
          dataClassification: 'Confidential',
        }),
      });
      bucket = [];
      index += 1;
    }
  }
  if (bucket.length > 0) {
    const first = at(bucket, 0);
    const product = generated.find((p) => p.businessCode === first);
    const code = `TEAM-G${pad(index + 1, 3)}`;
    teams.push({
      id: idFor('Team', code),
      businessCode: code,
      name: `Shared Services Squad ${pad(index + 1, 2)}`,
      productCodes: bucket,
      valueStreamCode: product?.valueStreamCode ?? 'VS-DATAAI',
      leadPersonCode: 'PPL-0005',
      internalFte: 8,
      contractorFte: 3,
      capacityPerIteration: 46,
      vacancyCount: 1,
      provenance: stamp(SOURCE_SYSTEMS.hcm, `HCM-TEAM-G${pad(index + 1, 4)}`, {
        meanAgeDays: 22,
        meanConfidence: 0.7,
        dataClassification: 'Confidential',
      }),
    });
  }

  return teams;
}

/** Contractor share of the total workforce, used by the workforce KPIs. */
export function contractorMix(teams: readonly Team[]): number {
  const internal = teams.reduce((sum, t) => sum + t.internalFte, 0);
  const contractor = teams.reduce((sum, t) => sum + t.contractorFte, 0);
  return internal + contractor === 0 ? 0 : round(contractor / (internal + contractor), 4);
}
